import { PureQueryOptions } from '@apollo/client';
import React from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { Links } from '../../lib/constants';
import { gqlTypes } from '../../types/gqlTypes';
import { useAppViewContext } from '../lib/AppViewContext';
import dogPlaceholderImage from '../../assets/images/dog-placeholder.png';

import styles from './ChipItem.module.scss';
import { DateTime } from 'luxon';

export interface ChipItemProps {
  chip: gqlTypes.miniChip;
  statusType: gqlTypes.ChipStatusType;
  refetchQueries?: PureQueryOptions[];
  searchMode?: boolean;
  showDateOwner?: boolean;
}

function getStatusLabel(statusType: gqlTypes.ChipStatusType): string {
  switch (statusType) {
    case gqlTypes.ChipStatusType.SHIPPED:
      return 'Stock';
    case gqlTypes.ChipStatusType.ACTIVE:
      return 'Registered';
    case gqlTypes.ChipStatusType.REVIEW:
      return 'Review';
    default:
      return '';
  }
}

function getStatusClass(statusType: gqlTypes.ChipStatusType): string {
  switch (statusType) {
    case gqlTypes.ChipStatusType.SHIPPED:
      return styles.statusShipped;
    case gqlTypes.ChipStatusType.ACTIVE:
      return styles.statusActive;
    case gqlTypes.ChipStatusType.REVIEW:
      return styles.statusReview;
    default:
      return '';
  }
}

export default function ChipItem({
  chip,
  statusType,
  refetchQueries,
  searchMode = false,
  showDateOwner = true,
}: ChipItemProps) {
  const history = useHistory();

  const isDesktop = useAppViewContext().isDesktop;

  const onClick = () => {
    if (statusType === gqlTypes.ChipStatusType.SHIPPED) {
      history.push(Links.register({ id: chip.id }));
    } else {
      history.push(Links.resellerChipDetails(chip.id, { forceBackBehavior: 'back' }, isDesktop));
    }
  };

  const photoUrl = chip.pet?.photos.first?.image.fullSize ?? dogPlaceholderImage;
  const registeredDate = DateTime.fromISO(chip.updatedAt).toFormat('LLL. d, y');

  const isRegistered = statusType === gqlTypes.ChipStatusType.ACTIVE;

  return (
    <div
      className={classNames(styles.row, { [styles.searchMode]: searchMode, [styles.hideDateOwner]: !showDateOwner })}
      onClick={onClick}
    >
      <div>
        <span className={styles.rowLabel}>Name</span>
        <img className={styles.photo} src={photoUrl} alt={chip.pet?.name} />
        <span>{chip.pet?.name}</span>
      </div>
      <div>
        <span className={styles.rowLabel}>Chip ID</span>
        {chip.serialNumber}
      </div>
      <div>
        <span className={styles.rowLabel}>Breed</span>
        {chip.pet?.breed?.name ?? 'No data'}
      </div>
      {showDateOwner && (
        <div>
          <span className={styles.rowLabel}>Date registered</span>
          {isRegistered ? registeredDate : ''}
        </div>
      )}
      {showDateOwner && (
        <div>
          <span className={styles.rowLabel}>Owner</span>
          {isRegistered && (
            <>
              {chip.latestContactHistoryEntry?.contactInfo.firstName}{' '}
              {chip.latestContactHistoryEntry?.contactInfo.lastName}
            </>
          )}
        </div>
      )}
      {searchMode && (
        <div>
          <span className={styles.rowLabel}>Status</span>
          <span className={classNames(styles.statusBadge, getStatusClass(statusType))}>
            {getStatusLabel(statusType)}
          </span>
        </div>
      )}
    </div>
  );
}
